import { useState } from "react";
import Sparkline from "../shared/Sparkline";
import ScoreRing from "../shared/ScoreRing";

function cn(...cs) { return cs.filter(Boolean).join(" "); }

function Toggle({ value, onChange }) {
    return (
        <button onClick={() => onChange(!value)}
                className={cn("w-9 h-5 rounded-full transition-colors relative flex-shrink-0", value ? "bg-blue-600" : "bg-gray-300")}>
            <span className={cn("absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform", value ? "translate-x-4" : "translate-x-0.5")}/>
        </button>
    );
}

function EmptyChart({ label, value }) {
    return (
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-4">
                <div className="w-32 flex-shrink-0">
                    <div className="text-xs text-gray-400 mb-0.5">{label}</div>
                    <div className="text-2xl font-semibold text-gray-900">{value}</div>
                </div>
                <div className="flex-1 relative h-24">
                    <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between text-[10px] text-gray-300 w-8">
                        {["1", "0.8", "0.6", "0.4", "0.2", "0"].map(v => <span key={v}>{v}</span>)}
                    </div>
                    <div className="absolute left-8 right-0 top-0 bottom-6">
                        {[0, 20, 40, 60, 80, 100].map(p => (
                            <div key={p} className="absolute left-0 right-0 border-t border-dashed border-gray-100" style={{ top: `${p}%` }}/>
                        ))}
                    </div>
                    <div className="absolute left-8 right-0 bottom-0 flex justify-between text-[10px] text-gray-300">
                        {["4 PM", "8 PM", "12 AM", "4 AM", "8 AM", "12 PM"].map(l => <span key={l}>{l}</span>)}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function DomainDetail({ domainName, clientData }) {
    const [banner, setBanner] = useState(true);
    const [timeRange, setTimeRange] = useState("24 Hours");
    const [aiCrawlers, setAiCrawlers] = useState("Do not block (allow crawlers)");
    const [robotsTxt, setRobotsTxt] = useState("Content Signals Policy");
    const [underAttack, setUnderAttack] = useState(false);
    const [devMode, setDevMode] = useState(false);
    const [kwTab, setKwTab] = useState("keywords");

    const domains = clientData?.domains_detail || [];
    const d = domains.find(x => x.name === domainName) || domains[0];
    const team = clientData?.team || [];
    const lastAuditTime = clientData?.auditLogs?.[0]?.time || "recently";

    if (!d) return (
        <main className="flex-1 flex items-center justify-center bg-white">
            <p className="text-sm text-gray-400">No domain data available.</p>
        </main>
    );

    return (
        <main className="flex-1 overflow-y-auto bg-white">
            {banner && (
                <div className="border-b border-blue-100 bg-blue-50 px-6 py-2.5 flex items-center gap-3 text-sm">
                    <span className="text-gray-700">Advanced Certificate Manager: Unlock more control and flexibility for your Certificates and SSL/TLS settings.</span>
                    <button className="ml-auto px-3 py-1 border border-gray-300 rounded text-xs font-medium hover:bg-white">Activate</button>
                    <button onClick={() => setBanner(false)} className="text-gray-400 hover:text-gray-700">
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            )}

            <div className="flex">
                {/* Main content */}
                <div className="flex-1 p-6 min-w-0">
                    <div className="text-xs text-gray-400 mb-1">Overview</div>
                    <h1 className="text-2xl font-semibold text-gray-900 mb-1">{d.name}</h1>
                    <p className="text-xs text-gray-400 mb-4">Monitor and configure how HerStorySEO processes your web traffic.</p>

                    <div className="flex items-center gap-2 mb-5">
                        <button className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded text-xs text-gray-600 hover:bg-gray-50">
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                            Review HerStorySEO fundamentals
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-200 rounded text-xs text-gray-400 hover:bg-gray-50">
                            + Add tag
                        </button>
                    </div>

                    {/* Time range */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex gap-1">
                            {["24 Hours", "7 Days", "30 Days"].map(t => (
                                <button key={t} onClick={() => setTimeRange(t)}
                                        className={cn("px-2.5 py-1 rounded text-xs transition-colors",
                                            timeRange === t ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100")}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <EmptyChart label="Unique Visitors" value={d.uniqueVisitorsFormatted || "0"}/>
                    <EmptyChart label="Total Requests" value="0"/>

                    {/* Keywords / Issues tabs */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex border-b border-gray-100">
                            {["keywords", "issues"].map(t => (
                                <button key={t} onClick={() => setKwTab(t)}
                                        className={cn("px-4 py-2.5 text-xs font-medium capitalize transition-colors border-b-2 -mb-px",
                                            kwTab === t ? "border-blue-600 text-blue-600" : "border-transparent text-gray-400 hover:text-gray-700")}>
                                    {t === "keywords"
                                        ? `Top Keywords (${d.seo?.topKeywords?.length ?? 0})`
                                        : `Issues (${d.seo?.issues?.length ?? 0})`}
                                </button>
                            ))}
                        </div>

                        {kwTab === "keywords" && (
                            <table className="w-full text-xs">
                                <thead>
                                <tr className="border-b border-gray-100 bg-gray-50">
                                    <th className="text-left px-4 py-2 text-gray-400 font-medium">Keyword</th>
                                    <th className="text-center px-3 py-2 text-gray-400 font-medium">Position</th>
                                    <th className="text-right px-3 py-2 text-gray-400 font-medium">Search Vol.</th>
                                    <th className="text-right px-4 py-2 text-gray-400 font-medium">Change</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(d.seo?.topKeywords || []).map((kw, i) => (
                                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                        <td className="px-4 py-2.5 text-gray-800 font-medium">{kw.keyword}</td>
                                        <td className="px-3 py-2.5 text-center">
                                            <span className={cn("inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold",
                                                kw.position <= 3 ? "bg-green-100 text-green-700" : kw.position <= 10 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500")}>
                                                {kw.position}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-gray-500">{kw.searchVolume}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            <span className={cn("font-semibold", kw.change > 0 ? "text-green-600" : kw.change < 0 ? "text-red-500" : "text-gray-400")}>
                                                {kw.change > 0 ? `▲ +${kw.change}` : kw.change < 0 ? `▼ ${kw.change}` : "—"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}

                        {kwTab === "issues" && (
                            <div className="p-4 space-y-2">
                                {(d.seo?.issues || []).map((iss, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <span className={cn("text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded flex-shrink-0",
                                            iss.type === "warning" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700")}>
                                            {iss.type}
                                        </span>
                                        <span className="text-xs text-gray-700">{iss.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right panel */}
                <div className="w-68 flex-shrink-0 border-l border-gray-200 p-5 space-y-6" style={{ width: "272px" }}>
                    {/* SEO score */}
                    <div>
                        <div className="font-semibold text-gray-900 text-xs mb-3">SEO Health Score</div>
                        <div className="flex items-center gap-3">
                            <ScoreRing score={d.overallSeoScore} size={56}/>
                            <div>
                                <div className={cn("text-sm font-bold",
                                    d.overallSeoScore >= 80 ? "text-green-600" : d.overallSeoScore >= 60 ? "text-amber-600" : "text-red-500")}>
                                    {d.overallSeoScore >= 80 ? "Excellent" : d.overallSeoScore >= 60 ? "Fair" : "Needs Work"}
                                </div>
                                <div className="text-[11px] text-gray-400">Updated {lastAuditTime}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            {[
                                { l: "Domain Auth.", v: d.seo?.domainAuthority, unit: "/100" },
                                { l: "Indexed Pages", v: d.seo?.indexedPages, unit: "pgs" },
                                { l: "Crawl Errors",  v: d.seo?.crawlErrors, alert: d.seo?.crawlErrors > 0 },
                                { l: "Core Vitals",   v: d.seo?.coreWebVitals === "Good" ? "Good" : "Fair" },
                            ].map(s => (
                                <div key={s.l} className="bg-gray-50 rounded p-2">
                                    <div className="text-[10px] text-gray-400 mb-0.5">{s.l}</div>
                                    <div className={cn("text-sm font-bold", s.alert ? "text-red-500" : "text-gray-800")}>
                                        {s.v} {s.unit && <span className="text-[10px] font-normal text-gray-400">{s.unit}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* DNS */}
                    <div>
                        <div className="font-semibold text-gray-900 text-xs mb-2">DNS</div>
                        <div className="flex items-center gap-1.5 text-xs mb-1">
                            <span className="text-gray-400">DNS Setup:</span>
                            <span className="font-medium text-gray-800">Full</span>
                        </div>
                        <button className="text-blue-600 text-xs hover:underline">DNS Records</button>
                    </div>

                    {/* AI crawlers */}
                    <div>
                        <div className="font-semibold text-gray-900 text-xs mb-3">Control AI crawlers</div>
                        <div className="mb-3">
                            <div className="font-medium text-gray-700 text-xs mb-0.5">Block AI training bots</div>
                            <div className="text-[11px] text-gray-400 mb-1.5">Deploys a managed rule to block bots that we categorize as AI training crawlers.</div>
                            <select value={aiCrawlers} onChange={e => setAiCrawlers(e.target.value)}
                                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-700 bg-white outline-none focus:border-blue-400">
                                <option>Do not block (allow crawlers)</option>
                                <option>Block AI training bots</option>
                            </select>
                        </div>
                        <div>
                            <div className="font-medium text-gray-700 text-xs mb-0.5">Manage your robots.txt</div>
                            <div className="text-[11px] text-gray-400 mb-1.5">Set how HerStorySEO manages your robots.txt file.</div>
                            <select value={robotsTxt} onChange={e => setRobotsTxt(e.target.value)}
                                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-700 bg-white outline-none focus:border-blue-400">
                                <option>Content Signals Policy</option>
                                <option>Block all crawlers</option>
                                <option>Allow all crawlers</option>
                            </select>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                        <div className="font-semibold text-gray-900 text-xs mb-3">Quick Actions</div>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-xs font-medium text-gray-700">Under Attack Mode</span>
                                    <Toggle value={underAttack} onChange={setUnderAttack}/>
                                </div>
                                <p className="text-[11px] text-gray-400">Show visitors a challenge when they visit your site.</p>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-xs font-medium text-gray-700">Development Mode</span>
                                    <Toggle value={devMode} onChange={setDevMode}/>
                                </div>
                                <p className="text-[11px] text-gray-400">Temporarily bypass your cache to see live changes.</p>
                            </div>
                        </div>
                    </div>

                    {/* Team */}
                    {team.length > 0 && (
                        <div>
                            <div className="font-semibold text-gray-900 text-xs mb-2">Team</div>
                            <div className="space-y-1.5">
                                {team.map(m => (
                                    <div key={m.email} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-[10px] font-bold flex-shrink-0">
                                            {m.email[0].toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[11px] text-gray-700 truncate">{m.email}</div>
                                        </div>
                                        <span className={cn("text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
                                            m.status === "Active" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400")}>
                                            {m.role}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
